import { Component, Inject, OnDestroy, ViewChild } from "@angular/core";
import { FormBuilder, FormControl, FormGroup } from "@angular/forms";
import { MatAutocompleteTrigger } from "@angular/material/autocomplete";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatSelectChange } from "@angular/material/select";
import { MatSnackBar } from "@angular/material/snack-bar";
import { AcaoComercial } from "app/acao-comercial/model/acao-comercial.model";
import { AcaoComercialService } from "app/acao-comercial/services/acao-comercial.service";
import { ParceiroCartaConsorcioModel } from "app/auxiliares/models/parceiro-carta-consorcio.model";
import { PrimeiroContato } from "app/auxiliares/models/primeiro-contato.model";
import { StatusMovimentacao } from "app/auxiliares/models/status-movimentacao.model";
import { ParceiroCartaConsorcioService } from "app/auxiliares/services/parceiro-carta-consorcio-service";
import { PrimeiroContatoService } from "app/auxiliares/services/primeiro-contato.service";
import { StatusMovimentacaoService } from "app/auxiliares/services/status-movimentacao.service";
import { BaseV2Component } from "app/base-module/base-component-v2/base-component-v2.component";
import { UsuarioFilter } from "app/base-module/base-model/usuario-filter.model";
import { AuthService } from "app/base-module/base-service/auth.service";
import { UsuarioService } from "app/base-module/base-service/usuario.service";
import { UtilitesService } from "app/base-module/base-service/utilites.service";
import { ClienteModel } from "app/clientes/models/cliente.model";
import { ClienteService } from "app/clientes/services/cliente.service";
import { BoletaConsorcio } from "app/consorcio/models/boleta-consorcio.model";
import { FiltrosBoletaConsorcioQueryParams } from "app/consorcio/models/filtros-lista-boleta-consorcio-query-params.model";
import { TipoConsorcio } from "app/consorcio/models/tipo-consorcio.model";
import { TipoLance } from "app/consorcio/models/tipo-lance.model";
import { TipoLanceCartaConsorcioService } from "app/consorcio/services/tipo-lance-carta-consorcio.service";
import { TipoInvestimento } from "app/constants";
import { ParceiroCreditoModel } from "app/credito/model/parceiro-credito.model";
import { TabelaComissionamentoModel } from "app/credito/model/tabela-comissionamento.model";
import { TipoOperacaoCreditoModel } from "app/credito/model/tipo-operacao-credito.model";
import { ParceiroCreditoService } from "app/credito/services/parceiro-credito.service";
import { TabelaComissionamentoService } from "app/credito/services/tabela-comissionamento.service";
import { TipoOperacaoCreditoService } from "app/credito/services/tipo-operacao-credito.service";
import { EscritorioService } from "app/empreendimento/services/escritorio.service";
import { Escritorio } from "app/empresas/models/escritorio.model";
import { OperadorPlanoSaudeModel } from "app/plano-saude/model/operador-plano-saude.model";
import { ParceiroPlanoSaudeModel } from "app/plano-saude/model/parceiro-plano-saude.model";
import { PrazoPlanoSaudeModel } from "app/plano-saude/model/prazo-plano-saude.model";
import { TipoOperacaoPlanoSaudeModel } from "app/plano-saude/model/tipo-operacao-plano-saude.model";
import { OperadorPlanoSaudeService } from "app/plano-saude/services/operador-plano-saude.service";
import { ParceiroPlanoSaudeService } from "app/plano-saude/services/parceiro-plano-saude.service";
import { PrazoPlanoSaudeService } from "app/plano-saude/services/prazo-plano-saude.service";
import { TipoOperacaoPlanoSaudeService } from "app/plano-saude/services/tipo-operacao-plano-saude.service";
import { ProdutoRendaFixaModel } from "app/renda-fixa/model/produto-rendafixa.model";
import { ProdutoRendaFixaService } from "app/renda-fixa/services/produto-rendafixa.service";
import { Estrategia } from "app/renda-variavel/models/estrategia.model";
import { StatusOrdemModel } from "app/renda-variavel/models/status-ordem.model";
import { EstrategiaService } from "app/renda-variavel/services/estrategia.service";
import { StatusOrdemService } from "app/renda-variavel/services/status-ordem.service";
import { UserRoleEnum } from "app/usuarios/models/user-role.enum";
import { Usuario } from "app/usuarios/models/user.model";
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    filter,
    finalize,
    map,
    Observable,
    of,
    startWith,
    Subject,
    switchMap,
    takeUntil,
    tap,
} from "rxjs";
import { DateUtils } from "src/utils/date-utils";
import { TipoConsorcioService } from "../../consorcio/services/tipo-consorcio.service";
import { FiltrosBoletaQueryParams } from "../models/filtros-boleta-query-params.model";

export interface FiltrarBoletaProps {
    id: number;
    titulo: string;
    status_ordem: boolean;
    status_boleta: boolean;
    tipo_produto: string;
    tipo: string; // TODO: Tech debt: We should use TipoInvestimento everywhere we pass tipo to this component
    initialFilters?: FiltrosBoletaQueryParams;
    isClearing?: boolean;
}

interface FilterFieldsConfig {
    produto: boolean;
    compraVenda: boolean;
    homebroker: boolean;
    acaoComercial: boolean;
    classificacao: boolean;
    estrategia: boolean;
    tipoConsorcio: boolean;
    tipoLance: boolean;
    parceiroConsorcio: boolean;
    prazoPlanoSaude: boolean;
    tipoOperacaoPlanoSaude: boolean;
    parceiroPlanoSaude: boolean;
    dataAtualizacao: boolean;
    dataRetorno: boolean;
    prazoCredito: boolean;
    tipoOperacaoCredito: boolean;
    parceiroCredito: boolean;
    operadoraCredito: boolean;
    dateLabel: "operacao" | "fechamento";
    setDefaultDates: boolean;
    setDefaultUpdateDates: boolean;
    setDefaultReturnDates: boolean;
    limparFiltrosButton: boolean;
}

const DEFAULT_FILTER_CONFIG: FilterFieldsConfig = {
    produto: false,
    compraVenda: false,
    homebroker: false,
    acaoComercial: false,
    classificacao: false,
    estrategia: false,
    tipoConsorcio: false,
    tipoLance: false,
    parceiroConsorcio: false,
    prazoPlanoSaude: false,
    tipoOperacaoPlanoSaude: false,
    parceiroPlanoSaude: false,
    dataAtualizacao: false,
    dataRetorno: false,
    prazoCredito: false,
    tipoOperacaoCredito: false,
    parceiroCredito: false,
    operadoraCredito: false,
    dateLabel: "operacao",
    setDefaultDates: true,
    setDefaultUpdateDates: false,
    setDefaultReturnDates: false,
    limparFiltrosButton: false,
};

const FILTER_CONFIG: Partial<Record<string, Partial<FilterFieldsConfig>>> = {
    [TipoInvestimento.RENDA_FIXA]: {
        produto: true,
        compraVenda: true,
    },
    [TipoInvestimento.RENDA_VARIAVEL]: {
        homebroker: true,
        acaoComercial: true,
        classificacao: true,
        estrategia: true,
    },
    [TipoInvestimento.CONSORCIO]: {
        tipoConsorcio: true,
        tipoLance: true,
        parceiroConsorcio: true,
        dateLabel: "fechamento",
        limparFiltrosButton: true,
        dataAtualizacao: true,
        setDefaultDates: false,       
        setDefaultUpdateDates: false,
    },
    [TipoInvestimento.PLANO_SAUDE]: {
        prazoPlanoSaude: true,
        tipoOperacaoPlanoSaude: true,
        dataAtualizacao: true,
        parceiroPlanoSaude: true,
        dataRetorno: false,
        dateLabel: "fechamento",
        setDefaultUpdateDates: false,
        setDefaultDates: false,
        setDefaultReturnDates: false,
        limparFiltrosButton: false,
    },
    [TipoInvestimento.CREDITO]: {
        prazoCredito: true,
        tipoOperacaoCredito: true,
        parceiroCredito: true,
        operadoraCredito: true,
        dateLabel: "fechamento",
        dataAtualizacao: true,        
        setDefaultDates: false,       
        setDefaultUpdateDates: false,
        limparFiltrosButton: false,
    },
    [TipoInvestimento.PATRIMONIO]: {
        dateLabel: "fechamento",
        setDefaultDates: false,
        dataAtualizacao: true,        
        setDefaultUpdateDates: false,
        limparFiltrosButton: false,
    },
    [TipoInvestimento.COE]: {
        compraVenda: true,
    },
};

@Component({
    selector: "dialog-filtrar-boleta",
    templateUrl: "./filtrar-boleta.component.html",
    styleUrls: ["./filtrar-boleta.component.scss"],
    providers: [
        PrimeiroContatoService,
        EstrategiaService,
        EscritorioService,
        ProdutoRendaFixaService,
        OperadorPlanoSaudeService,
        PrazoPlanoSaudeService,
        TipoOperacaoPlanoSaudeService,
        ParceiroPlanoSaudeService,
        TabelaComissionamentoService,
        TipoOperacaoCreditoService,
        ParceiroCreditoService,
        ParceiroCartaConsorcioService,
    ],
})
export class FiltrarBoletaComponent
    extends BaseV2Component<BoletaConsorcio, FiltrosBoletaConsorcioQueryParams>
    implements OnDestroy
{
    // Expose constants to template
    readonly TipoInvestimento = TipoInvestimento;
    readonly UserRole = UserRoleEnum;

    config: FilterFieldsConfig = DEFAULT_FILTER_CONFIG;

    private destroy$ = new Subject<void>();
    private clientesCache = new Map<string, ClienteModel>();
    filtrosForm!: FormGroup<{
        cliente_id: FormControl<string | null>;
        status_id: FormControl<string | null>;
        assessor_id: FormControl<string | null>;
        escritorio_id: FormControl<string | null>;
        status_ordem_id: FormControl<string | null>;
        homebroker: FormControl<string | null>;
        acao_comercial_id: FormControl<string | null>;
        primeiro_contato_id: FormControl<string | null>;
        estrategia_id: FormControl<string | null>;
        ativos: FormControl<string | null>;
        tipo_consorcio_id: FormControl<string | null>;
        compra_venda: FormControl<string | null>;
        produto_id: FormControl<string | null>;
        data_operacao_inicio: FormControl<string | null>;
        data_operacao_fim: FormControl<string | null>;
        prazo_id: FormControl<string | null>;
        tipo_operacao_id: FormControl<string | null>;
        operador_id: FormControl<string | null>;
        parceiro_credito_id: FormControl<string | null>;
        parceiro_id: FormControl<string | null>;
        data_atualizacao_inicio: FormControl<string | null>;
        data_atualizacao_fim: FormControl<string | null>;
        data_retorno_inicio: FormControl<string | null>;
        data_retorno_fim: FormControl<string | null>;
        prazo_credito: FormControl<string | null>;
        tipo_operacao_credito_id: FormControl<string | null>;
        tabela_comissionamento_id: FormControl<string | null>;
        tipo_lance_id: FormControl<string | null>;
        parceiro_consorcio_id: FormControl<string | null>;
    }>;

    loadingSpinner: boolean = false;
    loadingSpinnerStatus: boolean = false;
    loadingSpinnerStatusOrdem: boolean = false;
    loadingSpinnerAssessor: boolean = false;
    loadingSpinnerEscritorio: boolean = false;
    loadingSpinnerAcaoComercial: boolean = false;
    loadingSpinnerClassificacao: boolean = false;
    loadingSpinnerEstrategia: boolean = false;
    loadingSpinnerTipo: boolean = false;
    loadingSpinnerProduto: boolean = false;
    loadingSpinnerTipoLance: boolean = false;
    filteredClientOptions!: Observable<ClienteModel[]>;
    listStatus: StatusMovimentacao[] = [];
    listStatusOrdem: StatusOrdemModel[] = [];
    listAssessor: Usuario[] = [];
    listEscritorios: Escritorio[] = [];
    listAcaoComercial: AcaoComercial[] = [];
    listClassificacao: PrimeiroContato[] = [];
    listEstrategia: Estrategia[] = [];
    listTipoConsorcio: TipoConsorcio[] = [];
    listProduto: ProdutoRendaFixaModel[] = [];
    listTipoLance: TipoLance[] = [];
    usuario: Usuario;

    loadingSpinnerOperadorPlanoSaude: boolean = false;
    loadingSpinnerOperadorAutocomplete = false;
    listOperador: OperadorPlanoSaudeModel[] = [];
    filteredOperadorOptions!: Observable<OperadorPlanoSaudeModel[]>;
    private operadoresCache = new Map<string, OperadorPlanoSaudeModel>();
    loadingSpinnerTipoOperacao: boolean = false;
    listTipoOperacao: TipoOperacaoPlanoSaudeModel[] = [];
    loadingSpinnerPrazo: boolean = false;
    listPrazo: PrazoPlanoSaudeModel[] = [];
    loadingSpinnerParceiro: boolean = false;
    listParceiro: ParceiroPlanoSaudeModel[] = [];

    loadingSpinnerTabelaComissionamentoCredito: boolean = false;
    listTabelaComissionamentoCredito: TabelaComissionamentoModel[] = [];
    loadingSpinnerTipoOperacaoCredito: boolean = false;
    listTipoOperacaoCredito: TipoOperacaoCreditoModel[] = [];
    loadingSpinnerParceiroCredito: boolean = false;
    listParceiroCredito: ParceiroCreditoModel[] = [];

    loadingSpinnerParceiroConsorcio: boolean = false;
    listParceiroConsorcio: ParceiroCartaConsorcioModel[] = [];

    @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;

    constructor(
        public dialog: MatDialog,
        override snackBar: MatSnackBar,
        public dialogRef: MatDialogRef<FiltrarBoletaComponent>,
        private _clienteService: ClienteService,
        private _statusService: StatusMovimentacaoService,
        private _statusOrdemService: StatusOrdemService,
        private _usuarioService: UsuarioService,
        private _utilitesService: UtilitesService,
        @Inject(MAT_DIALOG_DATA) public data: FiltrarBoletaProps,
        private _authService: AuthService,
        public escritorioService: EscritorioService,
        public _primeiroService: PrimeiroContatoService,
        private _serviceAcaoComercial: AcaoComercialService,
        private _serviceEstrategia: EstrategiaService,
        private _tipoConsorcioService: TipoConsorcioService,
        private _serviceProduto: ProdutoRendaFixaService,
        private _operadorPlanoSaudeService: OperadorPlanoSaudeService,
        private _prazoService: PrazoPlanoSaudeService,
        private _tipoOperacaoService: TipoOperacaoPlanoSaudeService,
        private _parceiroService: ParceiroPlanoSaudeService,
        private _tabelaComissionamentoService: TabelaComissionamentoService,
        private _tipoOperacaoCreditoService: TipoOperacaoCreditoService,
        private _parceiroCreditoService: ParceiroCreditoService,
        private _tipoLanceCartaService: TipoLanceCartaConsorcioService,
        private _parceiroCartaConsorcioService: ParceiroCartaConsorcioService,
        private fb: FormBuilder
    ) {
        super(dialog, snackBar);

        this.initForm();

        this.filteredClientOptions = this.filtrosForm.get("cliente_id")!.valueChanges.pipe(
            filter((q): q is string => typeof q === "string" && q.trim() !== ""),
            tap(() => (this.loadingSpinner = true)),
            debounceTime(300),
            distinctUntilChanged(),
            switchMap((query: string) => {
                this.params.search = query;
                return this._clienteService.search(this.params).pipe(
                    map((res) => {
                        const result = res.data ?? [];
                        for (const cliente of result) {
                            this.clientesCache.set(cliente.id.toString(), cliente);
                        }
                        return result;
                    }),
                    finalize(() => (this.loadingSpinner = false)),
                    catchError(() => {
                        this.loadingSpinner = false;
                        return of([] as ClienteModel[]);
                    })
                );
            }),
            startWith([] as ClienteModel[]),
            takeUntil(this.destroy$)
        );

        if (this.data.tipo !== TipoInvestimento.PLANO_SAUDE) {
            this.filteredOperadorOptions = this.filtrosForm.get("operador_id")!.valueChanges.pipe(
                filter(
                    (q): q is string => typeof q === "string" && q.trim() !== "" && !this.operadoresCache.has(q)
                ),
                tap(() => (this.loadingSpinnerOperadorAutocomplete = true)),
                debounceTime(300),
                distinctUntilChanged(),
                switchMap((query: string) => {
                    const filterParams: UsuarioFilter = { search: query };
                    return this._usuarioService.search(filterParams).pipe(
                        map((res) => {
                            const result = (res.data ?? []).map((usuario: Usuario) => ({
                                id: usuario.id,
                                nome: usuario.name,
                            })) as OperadorPlanoSaudeModel[];
                            for (const op of result) {
                                this.operadoresCache.set(op.id.toString(), op);
                            }
                            return result;
                        }),
                        finalize(() => (this.loadingSpinnerOperadorAutocomplete = false)),
                        catchError(() => {
                            this.loadingSpinnerOperadorAutocomplete = false;
                            return of([] as OperadorPlanoSaudeModel[]);
                        })
                    );
                }),
                startWith([] as OperadorPlanoSaudeModel[]),
                takeUntil(this.destroy$)
            );
        }

        const strUser = this._authService.getUser();
        this.usuario = JSON.parse(strUser ? strUser : "{}");
        this.config = { ...DEFAULT_FILTER_CONFIG, ...FILTER_CONFIG[this.data.tipo] };

        this.getAllStatusMovimentacao();
        this.getOperadores();

        if (this.config.estrategia) this.getAllEstrategias();
        if (this.config.acaoComercial) this.getAllAcaoComercial();
        if (this.config.classificacao) this.getClassificacao();
        if (this.config.produto) this.getProdutosRendaFixa();
        if (this.config.tipoConsorcio) {
            this.getAllTipoConsorcio();
            this.getAllTipoLance();
        }
        if (this.config.parceiroConsorcio) this.getParceiroCartaConsorcio();
        if (this.config.prazoPlanoSaude) this.getPrazo();
        if (this.config.tipoOperacaoPlanoSaude) this.getTipoOperacao();
        if (this.config.parceiroPlanoSaude) this.getParceiro();
        if (this.config.tipoOperacaoCredito) this.getTipoOperacaoCredito();
        if (this.config.parceiroCredito) this.getParceiroCredito();
        if (this.config.operadoraCredito) this.getTabelaComissionamentoCredito();

        if (this.config.setDefaultDates) {
            this.filtrosForm.get("data_operacao_inicio")?.setValue(new Date().toISOString());
            this.filtrosForm.get("data_operacao_fim")?.setValue(new Date().toISOString());
        }
        if (this.config.setDefaultUpdateDates) {
            this.filtrosForm.get("data_atualizacao_inicio")?.setValue(new Date().toISOString());
            this.filtrosForm.get("data_atualizacao_fim")?.setValue(new Date().toISOString());
        }
        if (this.config.setDefaultReturnDates) {
            this.filtrosForm.get("data_retorno_inicio")?.setValue(new Date().toISOString());
            this.filtrosForm.get("data_retorno_fim")?.setValue(new Date().toISOString());
        }

        if (this.data.tipo === TipoInvestimento.CREDITO) {
            
            //this.filtrosForm.get("data_operacao_inicio")?.setValue(new Date().toISOString());
            //this.filtrosForm.get("data_operacao_fim")?.setValue(new Date().toISOString());
            this.getTabelaComissionamentoCredito();
            this.getTipoOperacaoCredito();
            this.getParceiroCredito();
        }
        if (this.data.status_ordem) this.getAllStatusOrdem();

        if (this.usuario.role_id !== UserRoleEnum.SOCIO_ASSESSOR) {
            this.getAllEscritorios();
            this.getAssessores();
        }

        this.applyInitialFilters();
    }

    /**
     * Destrói o componente
     */
    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
    /**
     * Exibe o texto no input do autocomplete a partir do valor atual do form control.
     * Quando o controle armazena o objeto do cliente, retorna o nome.
     *
     * @param cliente Valor atual do controle (ClienteModel ou string id)
     * @returns Texto a ser exibido no input
     */
    displayFn = (clienteId: string): string => {
        const cliente = this.clientesCache?.get(clienteId);
        return cliente?.nome ?? "";
    };

    /**
     * Exibe o texto no input do autocomplete a partir do valor atual do form control.
     * @param operadorId ID do operador
     * @returns Texto a ser exibido no input
     */
    displayOperadorFn = (operadorId: string): string => {
        const operador = this.operadoresCache?.get(operadorId);
        return operador?.nome ?? "";
    };

    /**
     * Fecha o modal sem aplicar filtros
     */
    fecharModal(): void {
        this.dialogRef.close("cancel");
    }

    /**
     * Verifica se existem filtros válidos
     */
    get hasValidFilters(): boolean {
        if (this.data.tipo === TipoInvestimento.CONSORCIO && this.filtrosForm) {
            const values = this.filtrosForm.value;
            return Object.values(values).some(
                (value) =>
                    value !== null && value !== undefined && (typeof value === "string" ? value.trim() !== "" : true)
            );
        }
        return false;
    }

    /**
     * Filtra os dados e fecha o modal
     */
    filtrar(): void {
        if (this.data.tipo !== TipoInvestimento.PLANO_SAUDE) {
            const operadorValue = this.filtrosForm.get("operador_id")?.value;
            if (operadorValue && !this.operadoresCache.has(operadorValue)) {
                this.showSnackMessage("Selecione um responsável válido da lista.", "error");
                return;
            }
        }

        // 🔥 O PULO DO GATO: Helper para formatar a data com segurança e matar o "NaN"
        const formatarDataSegura = (valorDaData: string | null | undefined): string | null => {
            if (!valorDaData) return null; // Barra nulo, undefined e string vazia ("")
            
            const data = new Date(valorDaData);
            if (isNaN(data.getTime())) return null; // Barra datas inválidas (ex: usuário digitou só metade)
            
            return this._utilitesService.formatDateToString(data);
        };

        this.dialogRef.close({
            status_id: this.filtrosForm.get("status_id")?.value,
            cliente_id: this.filtrosForm.get("cliente_id")?.value,
            
            // 👇 Usando a função segura para todas as datas!
            data_operacao_inicio: formatarDataSegura(this.filtrosForm.get("data_operacao_inicio")?.value),
            data_operacao_fim: formatarDataSegura(this.filtrosForm.get("data_operacao_fim")?.value),
            data_retorno_inicio: formatarDataSegura(this.filtrosForm.get("data_retorno_inicio")?.value),
            data_retorno_fim: formatarDataSegura(this.filtrosForm.get("data_retorno_fim")?.value),
            data_atualizacao_inicio: formatarDataSegura(this.filtrosForm.get("data_atualizacao_inicio")?.value),
            data_atualizacao_fim: formatarDataSegura(this.filtrosForm.get("data_atualizacao_fim")?.value),

            assessor_id: this.filtrosForm.get("assessor_id")?.value,
            escritorio_id: this.filtrosForm.get("escritorio_id")?.value,
            status_ordem_id: this.filtrosForm.get("status_ordem_id")?.value,
            homebroker: this.filtrosForm.get("homebroker")?.value,
            acao_comercial_id: this.filtrosForm.get("acao_comercial_id")?.value,
            primeiro_contato_id: this.filtrosForm.get("primeiro_contato_id")?.value,
            estrategia_id: this.filtrosForm.get("estrategia_id")?.value,
            ativos: this.filtrosForm.get("ativos")?.value,
            tipo_consorcio_id: this.filtrosForm.get("tipo_consorcio_id")?.value,
            compra_venda: this.filtrosForm.get("compra_venda")?.value,
            produto_id: this.filtrosForm.get("produto_id")?.value,
            prazo_id: this.filtrosForm.get("prazo_id")?.value,
            tipo_operacao_id:
                this.data.tipo === TipoInvestimento.CREDITO
                    ? this.filtrosForm.get("tipo_operacao_credito_id")?.value
                    : this.filtrosForm.get("tipo_operacao_id")?.value,
            operador_id: this.filtrosForm.get("operador_id")?.value,
            parceiro_id:
                this.data.tipo === TipoInvestimento.CREDITO
                    ? this.filtrosForm.get("parceiro_credito_id")?.value
                    : this.filtrosForm.get("parceiro_id")?.value,
            prazo_credito: this.filtrosForm.get("prazo_credito")?.value,
            tabela_comissionamento_id: this.filtrosForm.get("tabela_comissionamento_id")?.value,
            tipo_lance_id: this.filtrosForm.get("tipo_lance_id")?.value,
            parceiro_consorcio_id: this.filtrosForm.get("parceiro_consorcio_id")?.value,
        });
    }

    /**
     * Busca todos os status
     */
    getAllStatusMovimentacao(): void {
        this.loadingSpinnerStatus = true;

        this._statusService.getLista({ tipo_produto: this.data.tipo }).subscribe({
            next: (list) => {
                this.listStatus = list.data;
            },
            error: (_error) => {},
            complete: () => {
                this.loadingSpinnerStatus = false;
            },
        });
    }

    /**
     * Busca todas as estratégias
     */
    getAllEstrategias(): void {
        this.loadingSpinnerEstrategia = true;
        this._serviceEstrategia.getLista().subscribe({
            next: (list) => {
                this.listEstrategia = list.data;
            },
            error: (_error) => {},
            complete: () => {
                this.loadingSpinnerEstrategia = false;
            },
        });
    }

    /**
     * Busca todas as ações comerciais
     */
    getAllAcaoComercial(): void {
        this.loadingSpinnerAcaoComercial = true;

        this._serviceAcaoComercial.getAll({ no_paginate: true }).subscribe({
            next: (list) => {
                this.listAcaoComercial = list.data;
            },
            error: (_error) => {},
            complete: () => {
                this.loadingSpinnerAcaoComercial = false;
            },
        });
    }

    /**
     * Busca todos os status de ordem
     */
    getAllStatusOrdem(): void {
        this.loadingSpinnerStatusOrdem = true;
        this._statusOrdemService.getAll({ no_paginate: true }).subscribe({
            next: (list) => {
                this.listStatusOrdem = list.data;
            },
            error: (_error) => {},
            complete: () => {
                this.loadingSpinnerStatusOrdem = false;
            },
        });
    }

    /**
     * Seleciona um escritório e busca os assessores
     * @param event Evento de seleção
     */
    selectEscritorio(event: MatSelectChange): void {
        this.filtrosForm.get("escritorio_id")?.setValue(event.value);
        this.getAssessores(event.value);
    }

    /**
     * Busca todos os escritórios
     */
    getAllEscritorios(): void {
        this.loadingSpinnerEscritorio = true;
        this.escritorioService.getAllList({ no_paginate: true }).subscribe({
            next: (list) => {
                this.listEscritorios = list.data;
            },
            error: (_error) => {
                this.loadingSpinnerEscritorio = false;
            },
            complete: () => {
                this.loadingSpinnerEscritorio = false;
            },
        });
    }

    /**
     * Busca todos os assessores
     * @param escritorio_id ID do escritório
     */
    getAssessores(escritorio_id = ""): void {
        this.loadingSpinnerAssessor = true;
        this._usuarioService.getLista({ tipo_usuario: "A", escritorio_id: escritorio_id }).subscribe({
            next: (resquest) => {
                this.listAssessor = resquest.data;
            },
            error: (_error) => {
                this.loadingSpinnerAssessor = false;
            },
            complete: () => {
                this.loadingSpinnerAssessor = false;
            },
        });
    }

    /**
     * Busca todas as classificações
     */
    getClassificacao(): void {
        this.loadingSpinnerClassificacao = true;
        this._primeiroService.getLista().subscribe({
            next: (resquest) => {
                this.listClassificacao = resquest.data;
            },
            error: (_error) => {
                this.loadingSpinnerClassificacao = false;
            },
            complete: () => {
                this.loadingSpinnerClassificacao = false;
            },
        });
    }

    /**
     * Busca todos os tipos de consórcio
     */
    getAllTipoConsorcio(): void {
        this.loadingSpinnerTipo = true;
        this._tipoConsorcioService.getAll({ no_paginate: true }).subscribe({
            next: (list) => {
                this.listTipoConsorcio = list.data;
            },
            error: (_error) => {},
            complete: () => {
                this.loadingSpinnerTipo = false;
            },
        });
    }

    /**
     * Busca todos os tipos de lance
     */
    getAllTipoLance(): void {
        this.loadingSpinnerTipoLance = true;
        this._tipoLanceCartaService.getAll({ no_paginate: true }).subscribe({
            next: (list) => {
                this.listTipoLance = list.data;
            },
            error: (_error) => {},
            complete: () => {
                this.loadingSpinnerTipoLance = false;
            },
        });
    }

    /**
     * Busca todos os produtos de renda fixa
     */
    getProdutosRendaFixa(): void {
        this.loadingSpinnerProduto = true;
        this._serviceProduto.getAll({ inative: false }).subscribe({
            next: (list) => {
                if (this.listProduto?.length === 0) {
                    this.listProduto = list.data;
                } else {
                    this.listProduto.push(...list.data);
                }
            },
            error: (_error) => {},
            complete: () => {
                this.loadingSpinnerProduto = false;
            },
        });
    }

    /**
     * Busca operadores conforme o tipo de investimento.
     * Para PLANO_SAUDE usa OperadorPlanoSaudeService; demais tipos usam UsuarioService.getListaOperadores.
     */
    getOperadores(): void {
        if (this.data.tipo !== TipoInvestimento.PLANO_SAUDE) {
            return;
        }
        this.loadingSpinnerOperadorPlanoSaude = true;
        this._operadorPlanoSaudeService.getAll(this.params as unknown as Record<string, unknown>).subscribe({
            next: (response) => {
                this.listOperador = response.data;
            },
            error: () => {
                this.loadingSpinnerOperadorPlanoSaude = false;
            },
            complete: () => {
                this.loadingSpinnerOperadorPlanoSaude = false;
            },
        });
    }

    /**
     * Busca todos os parceiros
     */
    getParceiro(): void {
        this.loadingSpinnerParceiro = true;
        this._parceiroService.getAll(this.params as unknown as Record<string, unknown>).subscribe({
            next: (resquest) => {
                this.listParceiro = resquest.data;
            },
            error: (_error) => {
                this.loadingSpinnerParceiro = false;
            },
            complete: () => {
                this.loadingSpinnerParceiro = false;
            },
        });
    }

    /**
     * Busca todos os prazos
     */
    getPrazo(): void {
        this.loadingSpinnerPrazo = true;
        this._prazoService.getAll(this.params as unknown as Record<string, unknown>).subscribe({
            next: (resquest) => {
                this.listPrazo = resquest.data;
            },
            error: (_error) => {
                this.loadingSpinnerPrazo = false;
            },
            complete: () => {
                this.loadingSpinnerPrazo = false;
            },
        });
    }

    /**
     * Busca todos os tipos de operação
     */
    getTipoOperacao(): void {
        this.loadingSpinnerTipoOperacao = true;
        this._tipoOperacaoService.getAll(this.params as unknown as Record<string, unknown>).subscribe({
            next: (resquest) => {
                this.listTipoOperacao = resquest.data;
            },
            error: (_error) => {
                this.loadingSpinnerTipoOperacao = false;
            },
            complete: () => {
                this.loadingSpinnerTipoOperacao = false;
            },
        });
    }

    /**
     * Busca todos os tipos de operação de crédito
     */
    getTipoOperacaoCredito(): void {
        this.loadingSpinnerTipoOperacaoCredito = true;
        this._tipoOperacaoCreditoService.getAll(this.params as unknown as Record<string, unknown>).subscribe({
            next: (resquest) => {
                this.listTipoOperacaoCredito = resquest.data;
            },
            error: (_error) => {
                this.loadingSpinnerTipoOperacaoCredito = false;
            },
            complete: () => {
                this.loadingSpinnerTipoOperacaoCredito = false;
            },
        });
    }

    /**
     * Busca tabela de comissionamento
     */
    getTabelaComissionamentoCredito(): void {
        this.loadingSpinnerTabelaComissionamentoCredito = true;
        this._tabelaComissionamentoService.getAll(this.params as unknown as Record<string, unknown>).subscribe({
            next: (resquest) => {
                this.listTabelaComissionamentoCredito = resquest.data;
            },
            error: (_error) => {
                this.loadingSpinnerTabelaComissionamentoCredito = false;
            },
            complete: () => {
                this.loadingSpinnerTabelaComissionamentoCredito = false;
            },
        });
    }

    getParceiroCredito(): void {
        this.loadingSpinnerParceiroCredito = true;
        this._parceiroCreditoService.getAll(this.params as unknown as Record<string, unknown>).subscribe({
            next: (resquest) => {
                this.listParceiroCredito = resquest.data;
            },
            error: (_error) => {
                this.loadingSpinnerParceiroCredito = false;
            },
            complete: () => {
                this.loadingSpinnerParceiroCredito = false;
            },
        });
    }

    /**
     * Busca todos os parceiros de carta consórcio
     */
    getParceiroCartaConsorcio(): void {
        this.loadingSpinnerParceiroConsorcio = true;
        this._parceiroCartaConsorcioService.getAll({ no_paginate: true }).subscribe({
            next: (resquest) => {
                this.listParceiroConsorcio = resquest.data;
            },
            error: (_error) => {
                this.loadingSpinnerParceiroConsorcio = false;
            },
            complete: () => {
                this.loadingSpinnerParceiroConsorcio = false;
            },
        });
    }

    /**
     * Busca um cliente pelo ID e seta o id (string) no formulário.
     * Também popula as opções filtradas para permitir exibição na lista.
     *
     * @param cliente_id ID do cliente em formato string
     */
    getClienteById(cliente_id: string): void {
        this.loadingSpinner = true;
        this._clienteService.getById(cliente_id).pipe(takeUntil(this.destroy$)).subscribe({
            next: (response) => {
                const cliente = response?.data;
                this.clientesCache.set(cliente_id, cliente);
                this.filtrosForm.get("cliente_id")?.setValue(cliente.id.toString());
                if (cliente) {
                    this.filteredClientOptions = of([cliente]);
                }
                this.fecharAutocompletePainel();
            },
            complete: () => {
                this.loadingSpinner = false;
            },
        });
    }

    /**
     * Inicializa o formulário
     */
    initForm(): void {
        this.filtrosForm = this.fb.group({
            cliente_id: new FormControl<string | null>(null),
            status_id: new FormControl<string | null>(null),
            assessor_id: new FormControl<string | null>(null),
            escritorio_id: new FormControl<string | null>(null),
            status_ordem_id: new FormControl<string | null>(null),
            homebroker: new FormControl<string | null>(null),
            acao_comercial_id: new FormControl<string | null>(null),
            primeiro_contato_id: new FormControl<string | null>(null),
            estrategia_id: new FormControl<string | null>(null),
            ativos: new FormControl<string | null>(null),
            tipo_consorcio_id: new FormControl<string | null>(null),
            compra_venda: new FormControl<string | null>(null),
            produto_id: new FormControl<string | null>(null),
            data_operacao_inicio: new FormControl<string | null>(null),
            data_operacao_fim: new FormControl<string | null>(null),
            prazo_id: new FormControl<string | null>(null),
            tipo_operacao_id: new FormControl<string | null>(null),
            operador_id: new FormControl<string | null>(null),
            parceiro_id: new FormControl<string | null>(null),
            data_atualizacao_inicio: new FormControl<string | null>(null),
            data_atualizacao_fim: new FormControl<string | null>(null),
            data_retorno_inicio: new FormControl<string | null>(null),
            data_retorno_fim: new FormControl<string | null>(null),
            prazo_credito: new FormControl<string | null>(null),
            tipo_operacao_credito_id: new FormControl<string | null>(null),
            tabela_comissionamento_id: new FormControl<string | null>(null),
            parceiro_credito_id: new FormControl<string | null>(null),
            tipo_lance_id: new FormControl<string | null>(null),
            parceiro_consorcio_id: new FormControl<string | null>(null),
        });
    }

    /**
     * Fecha as opções do autocomplete
     */
    fecharAutocompletePainel(): void {
        this.autocompleteTrigger?.closePanel();
    }

    /**
     * Limpa todos os filtros do formulário
     */
    limpar(): void {
        this.filtrosForm?.reset();
        this.data.isClearing = true;

        this.dialogRef.close({});
    }

    /**
     * Aplica filtros iniciais ao FormGroup para tipo Consórcio
     * @param initialFilters Filtros iniciais passados via DialogData
     */
    applyInitialFilters(): void {
        const initialFilters = this.data.initialFilters;
        if (!this.filtrosForm || !initialFilters) {
            return;
        }

        const formatDate = (dateStr?: string): string | undefined => {
            if (!dateStr) {
                return undefined;
            }
            const localDate = DateUtils.parseLocalDate(dateStr);

            return localDate?.toISOString() ?? dateStr;
        };

        initialFilters.data_operacao_inicio = formatDate(initialFilters.data_operacao_inicio);
        initialFilters.data_operacao_fim = formatDate(initialFilters.data_operacao_fim);
        initialFilters.data_retorno_inicio = formatDate(initialFilters.data_retorno_inicio);
        initialFilters.data_retorno_fim = formatDate(initialFilters.data_retorno_fim);

        if (initialFilters.cliente_id) {
            this.getClienteById(initialFilters.cliente_id);
        }

        if (initialFilters.operador_id && this.data.tipo !== TipoInvestimento.PLANO_SAUDE) {
            const operadorId = initialFilters.operador_id;
            delete initialFilters.operador_id; // Evita race condition quando atualiza o formulário
            this.getOperadorById(Number(operadorId), operadorId);
        }

        this.filtrosForm.patchValue(initialFilters);
    }

    /**
     * Busca um operador pelo ID e seta o id (string) no formulário.
     * @param operadorId ID do operador
     * @param operadorIdStr ID do operador em formato string
     */
    private getOperadorById(operadorId: number, operadorIdStr: string): void {
        this._usuarioService
            .getById(operadorId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    const usuario = response.data;
                    if (usuario) {
                        this.operadoresCache.set(operadorIdStr, {
                            id: usuario.id,
                            nome: usuario.name,
                        } as OperadorPlanoSaudeModel);
                        this.filtrosForm.get("operador_id")?.setValue(operadorIdStr, { emitEvent: false });
                    }
                },
                error: () => {
                    this.loadingSpinnerOperadorAutocomplete = false;
                    this.showSnackMessage("Erro ao buscar operador.", "error");
                },
            });
    }
}
